import { Grid, Stack, Typography } from '@mui/material';

import { ChangeEvent, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { AutoComplete } from 'features/common/AutoCompleteSelect/AutoComplete';

import { getPromptTagsData, postPromptTagsData } from '../../api';
import { IPromptProps, ITagsOptions } from '../../api/type-def';

import { PROMPT_TITLE_MESSAGE_LABEL } from '../PromptBuilderConstants';

import styles from './PromptDetails.module.scss';

interface IPromptDetails {
	isEdit: boolean;
	onPromptChangeHandler: (
		keyValue: string,
		event: React.ChangeEvent<HTMLInputElement>,
	) => void;
	onFieldValueChangeHandler: (key: string, value: any) => void;
}
const PromptDetails = (props: IPromptDetails) => {
	const { isEdit, onPromptChangeHandler, onFieldValueChangeHandler } = props;
	const { OK } = HTTP_STATUS_CODES;
	const prompt: IPromptProps = useSelector(
		(state: RootState) => state.promptBuilder.prompt,
	);
	const [tagsOptions, setTagsOptions] = useState<ITagsOptions[]>([]);

	const getTagsList = async () => {
		const response: any = await getPromptTagsData();
		if (response?.statusCode === OK) {
			const tagsList = response.responseBody.data;
			const userTagsList = tagsList.filter(
				(element: ITagsOptions) => element.is_default === false,
			);
			const options = userTagsList.map((tag: any) => ({
				...tag,
				label: tag.name,
				id: tag.name,
				displayLabel: tag.name,
				checked: false,
			}));
			setTagsOptions(options);
		}
	};

	const addPromptTag = async (newlyAddedTag: string) => {
		const response = await postPromptTagsData(newlyAddedTag);
		if (response?.statusCode === OK) {
			const addedTag = {
				label: newlyAddedTag,
				id: newlyAddedTag,
				name: newlyAddedTag,
				displayLabel: newlyAddedTag,
				is_default: false,
			};
			setTagsOptions([...tagsOptions, addedTag]);
			onFieldValueChangeHandler('tags', [...prompt.tags, addedTag]);
		}
	};
	const onTagsSelect = (selectedTags: any[]) => {
		const newlyAdded = selectedTags.find((e) => e.isAdded);

		if (newlyAdded) {
			addPromptTag(newlyAdded.name);
		} else {
			onFieldValueChangeHandler('tags', selectedTags);
		}
	};

	/**
	 * @description method to set the key text on change
	 * @param key, event
	 */
	const onKeyTextChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event?.target?.value;
		onFieldValueChangeHandler('key', value);
	};

	useEffect(() => {
		getTagsList();
	}, []);

	const getUserTags = () => {
		const filteredTags = prompt.tags?.filter(
			(element: ITagsOptions) => element.is_default === false,
		);
		return (
			filteredTags?.map((e) => ({
				...e,
				id: e.id ? e.id : e.name,
				displayLabel: e.name,
			})) || []
		);
	};

	return (
		<Grid container className={styles['prompt-container']}>
			<Grid md={12}>
				<Stack>
					<TextFieldWithLabel
						label={toSentenceCase('Title')}
						inputProps={{
							'data-testid': 'prompt-builder-title',
						}}
						error={prompt.key === '' ? false : prompt?.key?.length <= 0}
						required
						fullWidth
						value={prompt?.key}
						onChange={onKeyTextChangeHandler}
						helperText="please enter Title"
						showLabel
						isFilled
						readOnly={!prompt?.is_draft || isEdit}
					/>

					<Typography
						variant="body2"
						className={styles['prompt-title-subheader']}
					>
						{PROMPT_TITLE_MESSAGE_LABEL}
					</Typography>
				</Stack>
			</Grid>
			<Grid item xs={12}>
				<TextFieldWithLabel
					label={toSentenceCase('Description')}
					inputProps={{
						'data-testid': 'prompt-builder-description',
					}}
					multiLine
					error={false}
					required
					value={prompt?.description}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						onPromptChangeHandler('description', event)
					}
					parentClass={styles['word-txt-position']}
					helperText=""
					showLabel
					className={styles['text-field-style']}
					positionClass={styles['word-limit-position']}
					wordLimit={420}
					isFilled
					readOnly={!prompt?.is_draft}
				/>
			</Grid>
			<Grid item xs={12}>
				<AutoComplete
					data={tagsOptions}
					onUserSelect={onTagsSelect}
					selectedData={getUserTags() || []}
					type="tags"
					key="tags"
					viewChange
					tagsCount={5}
					displayLabel="Tags"
					allowAdd
					disabled={!prompt?.is_draft}
				/>
			</Grid>
		</Grid>
	);
};
export default PromptDetails;
